import { merge } from '../../helper';

import isObject from '../../general/isObject';

import type { TypeRequestMethod, TypeRequestReason } from './types';
import { supportsRequestBody } from './utils';

import * as exUrl from '../url';

// ----------------------------------------------------------------------------

export type TypeRequestData = Document | XMLHttpRequestBodyInit | Record<string, any> | null | undefined;

export type TypeRequestXHR = XMLHttpRequest & {
    sendRequest: () => XMLHttpRequest;
};

export type TypeRequestConfig = {
    merge?: any;
    url?: string;
    method?: TypeRequestMethod;
    async?: boolean;
    autoSend?: boolean;
    data?: TypeRequestData;
    params?: Record<string, any>;
    headers?: Record<string, string>;
    responseType?: XMLHttpRequestResponseType;
    timeout?: number;
    withCredentials?: boolean;
    beforeSend?: (xhr: XMLHttpRequest) => void;
    responseParser?: (xhr: XMLHttpRequest) => any;
    fnProgress?: (evt: ProgressEvent) => void;
    fnProgressUpload?: (evt: ProgressEvent) => void;
    fnLoad?: (evt: ProgressEvent) => void;
    fnSuccess?: (data: any, reason: TypeRequestReason, xhr: XMLHttpRequest) => void;
    fnFail?: (xhr: XMLHttpRequest, reason: TypeRequestReason) => void;
    fnError?: (evt: ProgressEvent, reason: TypeRequestReason) => void;
    fnAbort?: (evt: ProgressEvent) => void;
    fnTimeout?: (evt: ProgressEvent) => void;
    fnComplete?: (evt: ProgressEvent, reason: TypeRequestReason, xhr: XMLHttpRequest) => void;
};

// ----------------------------------------------------------------------------

function xhr(config: TypeRequestConfig): TypeRequestXHR {
    const defConfig: TypeRequestConfig = {};
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    // Create request
    const request = new XMLHttpRequest() as TypeRequestXHR;

    // ------------------------------------------------------------------------

    //
    const method = defConfig.method?.toUpperCase() ?? 'GET';
    //
    let url = defConfig.url ?? '';
    //
    const async = defConfig.async ?? true;
    //
    let requestData = defConfig.data;

    // ------------------------------------------------------------------------

    // If using response type
    if (defConfig.responseType != null) {
        request.responseType = defConfig.responseType;
    }

    // If using timeout
    if (defConfig.timeout != null) {
        request.timeout = defConfig.timeout;
    }

    // If using with credentials
    if (defConfig.withCredentials != null) {
        request.withCredentials = defConfig.withCredentials;
    }

    // ------------------------------------------------------------------------

    // If using function progress
    if (typeof defConfig.fnProgress === 'function') {
        // Create event (progress)
        request.onprogress = function (evt): void {
            defConfig.fnProgress?.(evt);
        };
    }

    // If using function progress upload
    if (typeof defConfig.fnProgressUpload === 'function') {
        // Create event (progress upload)
        request.upload.onprogress = function (evt): void {
            defConfig.fnProgressUpload?.(evt);
        };
    }

    // ------------------------------------------------------------------------

    // If using function error or complete
    if (typeof defConfig.fnError === 'function' || typeof defConfig.fnComplete === 'function') {
        // Create event (error)
        request.onerror = function (evt): void {
            defConfig.fnError?.(evt, 'error');
            defConfig.fnComplete?.(evt, 'error', request);
        };
    }

    // If using function abort or complete
    if (typeof defConfig.fnAbort === 'function' || typeof defConfig.fnComplete === 'function') {
        // Create event (abort)
        request.onabort = function (evt): void {
            defConfig.fnAbort?.(evt);
            defConfig.fnComplete?.(evt, 'abort', request);
        };
    }

    // If using function timeout or complete
    if (typeof defConfig.fnTimeout === 'function' || typeof defConfig.fnComplete === 'function') {
        // Create event (timeout)
        request.ontimeout = function (evt): void {
            defConfig.fnTimeout?.(evt);
            defConfig.fnComplete?.(evt, 'timeout', request);
        };
    }

    // ------------------------------------------------------------------------

    // Create event (load)
    request.onload = function (evt: ProgressEvent): void {
        const currentRequest = evt.currentTarget as XMLHttpRequest;

        // If using function load
        if (typeof defConfig.fnLoad === 'function') {
            defConfig.fnLoad(evt);
        }

        // If there is ready state
        if (currentRequest.readyState !== XMLHttpRequest.DONE) {
            return;
        }

        // If status is success
        if (currentRequest.status >= 200 && currentRequest.status < 300) {
            let responseData = currentRequest.response;

            if (typeof defConfig.responseParser === 'function') {
                responseData = defConfig.responseParser(currentRequest);
            }

            defConfig.fnSuccess?.(responseData, 'success', currentRequest);
            defConfig.fnComplete?.(evt, 'success', currentRequest);
        }
        // If status is failed
        else {
            defConfig.fnFail?.(currentRequest, 'failed');
            defConfig.fnComplete?.(evt, 'failed', currentRequest);
        }
    };

    // ------------------------------------------------------------------------

    //
    if (isRequestObject(defConfig.params)) {
        const query = exUrl.parameter.set(defConfig.params);

        if (query !== '') {
            url += (url.includes('?') ? '&' : '?') + query;
        }
    }

    //
    if (!supportsRequestBody(method) && isRequestObject(requestData)) {
        const query = exUrl.parameter.set(requestData);

        if (query !== '') {
            url += (url.includes('?') ? '&' : '?') + query;
        }

        requestData = null;
    }
    //
    else if (supportsRequestBody(method) && isRequestObject(requestData)) {
        requestData = exUrl.parameter.set(requestData);
    }

    // ------------------------------------------------------------------------

    // Open
    request.open(method, url, async);

    // ------------------------------------------------------------------------

    //
    if (typeof defConfig.beforeSend === 'function') {
        defConfig.beforeSend(request);
    }

    //
    if (defConfig.headers) {
        for (const [key, value] of Object.entries(defConfig.headers)) {
            request.setRequestHeader(key, value);
        }
    }
    //
    else if (supportsRequestBody(method) && typeof requestData === 'string') {
        request.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    }

    // ------------------------------------------------------------------------

    //
    if (defConfig.autoSend ?? true) {
        if (requestData == null) {
            request.send();
        } else {
            request.send(requestData as Document | XMLHttpRequestBodyInit);
        }
    }

    // ------------------------------------------------------------------------

    // Return
    return request;
}

// ----------------------------------------------------------------------------

function isRequestObject(value: unknown): value is Record<string, any> {
    return isObject(value);
}

// ----------------------------------------------------------------------------

export { xhr };
